"use client";

import { MenuItemType, WidgetResultType } from '@akinon/next/types';
import { useAppDispatch, useAppSelector } from '@akinon/next/redux/hooks';
import { closeMobileMenu } from '@akinon/next/redux/reducers/header';
import { useEffect, useRef, useState } from 'react';
// import { useLocalization } from '@akinon/next/hooks';
import { Drawer } from '@theme/components/drawer';
import { Icon } from '@theme/components';
import { Link } from '@theme/components';
import clsx from 'clsx';
import { HeaderBandType } from './band';
import { MobileBandMenu } from './mobile-band-menu';
import { SearchForm } from './search/form';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@theme/components/shadcn/accordion';
import { Image } from '@akinon/next/components/image';
import { MenuList } from './menu-list';
import { useSession } from 'next-auth/react';
import { UserMenu } from './user-menu';
import { clearMobileSearchFocus } from '@theme/redux/reducers/headerSearch';

interface MobileMenuProps {
  menu: MenuItemType[];
  bandWidget: WidgetResultType<HeaderBandType>;
}

export const MobileMenuHeader = () => {
  const dispatch = useAppDispatch();

  return (
    <div className="flex items-center text-primary-black py-3 px-5 border-b border-[#CDCFCE]">
      <button type="button" className="size-6 flex items-center justify-center mr-2" onClick={() => dispatch(closeMobileMenu())}>
        <Icon name="y-close" size={13} />
      </button>
      <span className="text-xl font-medium">Menü</span>
      <UserMenu className="ml-auto" />
    </div>
  )
}

export default function MobileMenu(props: MobileMenuProps) {
  const { menu, bandWidget } = props;
  const dispatch = useAppDispatch();
  const [selectedSubMenu, setSelectedSubMenu] = useState<MenuItemType | null>(
    null
  );
  const isMobileMenuOpen = useAppSelector(
    (state) => state.header.isMobileMenuOpen
  );
  const shouldFocusSearch = useAppSelector(
    (state) => state.headerSearch.shouldFocusMobileSearch
  );
  const searchInputRef = useRef<HTMLInputElement>(null);

  // The drawer is opened from the header's search bar: focus the real search
  // field once the open transition (duration-500) is done.
  useEffect(() => {
    if (!shouldFocusSearch) return;

    if (!isMobileMenuOpen) {
      dispatch(clearMobileSearchFocus());
      return;
    }

    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
      dispatch(clearMobileSearchFocus());
    }, 550);

    return () => clearTimeout(timer);
  }, [isMobileMenuOpen, shouldFocusSearch, dispatch]);

  return (
    <Drawer
      portalId="drawer-root"
      open={isMobileMenuOpen}
      onClose={() => dispatch(closeMobileMenu())}
      HeaderComponent={<MobileMenuHeader />}
    >
      <div className="grow bg-[#FAF4ED] flex flex-col">
        <SearchForm className="my-6 mx-4" inputRef={searchInputRef} />
        <div className="relative overflow-x-hidden flex-1">
          <nav
            className={clsx('transition duration-500 transform text-primary-black flex flex-col min-h-full', {
              '-translate-x-full': selectedSubMenu
            })}
          >
            <ul className="px-4">
              {menu.map(
                (item, index) =>
                  item.label != null && (
                    <li key={index} className="py-4.5 text-sm font-medium border-b border-[#CDCFCE]">
                      <Link
                        href={item.url}
                        onClick={(e) => {
                          if (item.children.length > 0) {
                            e.preventDefault();
                            setSelectedSubMenu(item);
                          }
                        }}
                        className="flex items-center justify-between"
                      >
                        <span>{item.label}</span>
                        <Icon name="chevron-right" size={24} />
                      </Link>
                    </li>
                  )
              )}
            </ul>
            <MobileBandMenu bandWidget={bandWidget} />
            <Image
              src="/logo.svg"
              alt="Yatsan"
              width={130}
              height={25}
              className="mt-auto mx-auto py-6"
              unoptimized
              priority
            />
          </nav>
          <div
            className={clsx(
              'absolute top-0 left-0 right-0 bottom-0 overflow-y-hidden flex flex-col invisible opacity-0 transition duration-500 transform translate-x-full',
              {
                '!visible !opacity-100 !translate-x-0': selectedSubMenu
              }
            )}
          >
            <header className="flex items-center px-5 py-3 border-b border-[#CDCFCE]">
              <button className="px-0 size-6 mr-2" onClick={() => setSelectedSubMenu(null)}>
                <Icon name="chevron-left" size={24} />
              </button>
              <span className="text-xl font-medium">{selectedSubMenu?.label}</span>
            </header>
            <div className="flex-1 flex flex-col overflow-y-auto">
              <Accordion type="single" collapsible className='text-black-100 px-4 flex flex-col space-y-2 pt-5'>
                {selectedSubMenu?.children.map((child, index) => (
                  <AccordionItem value={child.label} key={index} className="border-none">
                    <AccordionTrigger icons={['chevron-down', 'chevron-up']} iconSize={24} className="h-12 flex items-center text-base !py-0 data-[state=closed]:font-normal">
                      <div className="flex items-center flex-1">
                        {child?.extra_context?.attributes?.main_image &&
                          <Image
                            src={child?.extra_context?.attributes?.main_image?.kwargs?.url}
                            alt={child.label}
                            width={48}
                            height={48}
                            className="flex-none max-w-12"
                            priority
                          />
                        }
                        <span className="px-6 flex-grow text-left">{child.label}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="flex flex-col pl-18 text-sm font-normal text-primary-black">
                        <Link className="py-2 leading-none" href={child.url}>Tümünü Gör</Link>
                        <MenuList LinkComponent={Link} item={child} className="flex flex-col space-y-6 divide-y divide-[#CDCFCE]" />
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              <MobileBandMenu bandWidget={bandWidget} />
              <Image
                src="/logo.svg"
                alt="Yatsan"
                width={130}
                height={25}
                className="mt-auto mx-auto py-6"
                unoptimized
                priority
              />
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  )
}
